import type {CustomerScanContext} from "../scan-model/CustomerScanContext.tsx";
import type {WorkloadInput} from "../../3-workload-analysis/model/WorkloadInput.ts";
import {scanCustomerVersions} from "./scanCustomerVersions.ts";
import {scanWorkloadJobs} from "./scanWorkloadJobs.ts";
import {scanWorkloadTaskHistory} from "./scanWorkloadTaskHistory.ts";
import {scanCustomerSharedJobs} from "./scanCustomerSharedJobs.ts";
import {scanWorkloads} from "../../3-workload-analysis/workload-scanner.ts";
import {performScoring, type ScoringResult} from "../../4-scoring/performScoring.ts";
import type {ProjectId} from "../../../metl-models/id/ProjectId.tsx";
import type {WorkloadJobHolder} from "../../3-workload-analysis/model/WorkloadJobHolder.ts";
import type {SharedJobScanResult} from "../../../analysis-process/3-perform-scan/scan-model/SharedJobResult.ts";
import type {Workload} from "../../3-workload-analysis/model/Workload.ts";

export interface WorkloadScanResult {
    workloadInputs: WorkloadInput[]
    workloads: Workload[]
    scoringResult: ScoringResult
    sharedJobs: SharedJobScanResult
    scanErrors: string[]
}

async function scanSchedulesForProject(ctx: CustomerScanContext, projectId: ProjectId) {
    const {api, log, logError} = ctx
    try {
        log(`Scanning schedules in ${projectId.groupName}/${projectId.projectName}`)
        const schedulesExport = await api.project(projectId).schedule.exportAll()
        ctx.metlVersion = schedulesExport.version
        ctx.dbEnvironment = schedulesExport.environment
        return schedulesExport.objects
    } catch(e) {
        logError(`An error occurred while scanning schedules in ${projectId.groupName}/${projectId.projectName}.`, e)
        return []
    }
}

export async function performWorkloadScan(ctx: CustomerScanContext): Promise<WorkloadScanResult> {
    try {
        ctx.log("Starting workload scan")
        
        // Scan shared jobs first so we can identify them in jobs
        const {result: sharedJobsResult, sharedJobsMap} = await scanCustomerSharedJobs(ctx)
        ctx.log(`Identified ${sharedJobsResult.list.length} shared job revisions`)
        
        // Scan versions to get the scan plan
        const versionsScanPlan = await scanCustomerVersions(ctx)
        ctx.log(`Identified ${versionsScanPlan.versions.length} versions to scan`)
        
        // Group versions by project
        const projectMap = new Map<string, {projectId: ProjectId, versions: typeof versionsScanPlan.versions}>()
        
        for (const version of versionsScanPlan.versions) {
            const key = `${version.id.groupName}/${version.id.projectName}`
            const existing = projectMap.get(key)
            if (existing) {
                existing.versions.push(version)
            } else {
                projectMap.set(key, {
                    projectId: {groupName: version.id.groupName, projectName: version.id.projectName},
                    versions: [version]
                })
            }
        }
        
        ctx.log(`Scanning ${projectMap.size} projects`)
        const {reportOne, complete} = ctx.startAction("Scanning projects", projectMap.size)
        
        const workloads: WorkloadInput[] = []
        
        for (const [key, {projectId, versions}] of projectMap) {
            try {
                ctx.log(`Starting scan of project ${key}`)
                
                // Scan all jobs across all versions in this project
                const allJobs: WorkloadJobHolder[] = []
                for (const versionScan of versions) {
                    const jobs = await scanWorkloadJobs(ctx, versionScan, sharedJobsMap)
                    allJobs.push(...jobs)
                }
                
                ctx.log(`Scanned ${allJobs.length} jobs in ${key}`)
                
                // Build jobsByName map
                const jobsByName: Record<string, WorkloadJobHolder> = {}
                for (const job of allJobs) {
                    jobsByName[job.name] = job
                }
                
                // Scan schedules for this project
                const schedules = await scanSchedulesForProject(ctx, projectId)
                ctx.log(`Scanned ${schedules.length} schedules in ${key}`)
                
                // Scan task history for this project
                const taskHistory = await scanWorkloadTaskHistory(ctx, ctx.api, projectId, allJobs)
                ctx.log(`Scanned ${taskHistory.length} task history batches in ${key}`)
                
                // Create WorkloadInput
                const workloadInput: WorkloadInput = {
                    projectId,
                    schedules,
                    jobs: allJobs,
                    jobsByName,
                    taskHistory,
                    sharedJobs: sharedJobsResult,
                    dbEnvironment: ctx.dbEnvironment,
                    metlVersion: ctx.metlVersion
                }
                
                workloads.push(workloadInput)
                ctx.log(`Completed scan of project ${key}`)
            } catch (e) {
                ctx.logError(`Failed to scan project ${key}`, e)
            } finally {
                reportOne()
            }
        }
        
        complete()
        ctx.log("Completed workload scan")
        
        // Identify and group workloads from the scanned data
        ctx.log("Identifying and grouping workloads")
        const identifiedWorkloads: Workload[] = []
        for (const workloadInput of workloads) {
            const projectWorkloads = scanWorkloads(workloadInput)
            identifiedWorkloads.push(...projectWorkloads)
            ctx.log(`Identified ${projectWorkloads.length} workloads in ${workloadInput.projectId.groupName}/${workloadInput.projectId.projectName}`)
        }
        ctx.log(`Total workloads identified: ${identifiedWorkloads.length}`)
        
        // Score all workloads
        ctx.log("Scoring workloads")
        let scoringResult: ScoringResult
        try {
            scoringResult = performScoring(identifiedWorkloads, workloads)
            ctx.log(`Scored ${scoringResult.workloadScores.length} workloads`)
            
            // Log summary statistics
            const scores = scoringResult.workloadScores.map(ws => ws.score.score)
            if (scores.length > 0) {
                const avgScore = scores.reduce((a, b) => a + b, 0) / scores.length
                const maxScore = Math.max(...scores)
                const minScore = Math.min(...scores)
                ctx.log(`Score range: ${minScore.toFixed(2)} to ${maxScore.toFixed(2)}, average: ${avgScore.toFixed(2)}`)
            }
        } catch (e) {
            ctx.logError("Failed to score workloads, continuing with empty scores", e)
            scoringResult = {
                workloadScores: [],
                totalWorkloads: identifiedWorkloads.length
            }
        }
        
        return {
            workloadInputs: workloads,
            workloads: identifiedWorkloads,
            scoringResult,
            sharedJobs: sharedJobsResult,
            scanErrors: ctx.scanErrors
        }
    } catch(e) {
        ctx.logError("An error occurred during scanning which has prevented continuing.", e)
        throw e
    }
}
